import { CollectionUtils } from "@rsm-hcd/javascript-core";
import { HeadingPriority } from "atoms/constants/heading-priority";
import Loader from "atoms/loaders/loader";
import Heading from "atoms/typography/heading";
import { PermissionScope } from "models/enumerations/notebooks/permission-scope";
import { RecipientType } from "models/enumerations/notebooks/recipient-type";
import NotebookPermissionManifestRecord from "models/view-models/notebooks/notebook-permission-manifest-record";
import NotebookResourcePermissionRecord from "models/view-models/notebooks/notebook-resource-permission-record";
import SharedEntry from "molecules/shared-entry/shared-entry";
import React from "react";
import { t } from "utilities/localization-utils";
const CSS_CLASS_NAME = "c-notebook-share-modal";

interface NotebookShareModalAccessProps {
    isLoadingPermissions: boolean;
    fullName: string;
    draftRecipientSaves: NotebookPermissionManifestRecord[];
    persistedNotebookPermissions: NotebookPermissionManifestRecord[];
    getParentheticalValue: (NotebookPermissionRecipientRecord) => string;
    onUpdateExistingPermission: (
        permission: NotebookResourcePermissionRecord,
        newPermissionScope: PermissionScope
    ) => void;
    onRemovePermission: (
        permissionManifest: NotebookPermissionManifestRecord
    ) => void;
    accessListType: "individual" | "group";
    scrollableElementRef: React.RefObject<HTMLDivElement>;
}
const NotebookShareModalAccessList = (props: NotebookShareModalAccessProps) => {
    const {
        isLoadingPermissions,
        fullName,
        draftRecipientSaves,
        persistedNotebookPermissions,
        getParentheticalValue,
        onUpdateExistingPermission,
        onRemovePermission,
        accessListType,
        scrollableElementRef,
    } = props;

    const isMatchingAccessType = (
        entry: NotebookPermissionManifestRecord,
        accessListType: "individual" | "group"
    ) => {
        const recipientType = entry.recipient?.type;
        if (accessListType === "individual") {
            return recipientType === RecipientType.User;
        }
        return (
            recipientType === RecipientType.Group ||
            recipientType === RecipientType.WorkGroup
        );
    };
    const draftSharedEntries = draftRecipientSaves.filter((entry) =>
        isMatchingAccessType(entry, accessListType)
    );
    const persistedSharedEntries = persistedNotebookPermissions.filter(
        (entry) => isMatchingAccessType(entry, accessListType)
    );

    const headerText =
        props.accessListType === "individual"
            ? "Individual Access"
            : "Group Access";
    return (
        <div className={`${CSS_CLASS_NAME}__shared-list ${accessListType}`}>
            <Heading
                cssClassName={`${CSS_CLASS_NAME}__shared-list-heading`}
                priority={HeadingPriority.Three}>
                {headerText}
            </Heading>
            <div className={`${CSS_CLASS_NAME}__shared-list-content`}>
                {accessListType === "individual" && (
                    <SharedEntry
                        key="owner"
                        name={fullName}
                        parentheticalValue={t("you")}
                        isSelf
                    />
                )}
                {draftSharedEntries.map((entry) => (
                    <SharedEntry
                        key={`${entry.recipient?.id}-${entry.recipient?.type}`}
                        name={entry.recipient.name}
                        parentheticalValue={getParentheticalValue(
                            entry.recipient!
                        )}
                        onPermissionChange={onUpdateExistingPermission}
                        onPermissionRemove={onRemovePermission}
                        manifest={entry}
                        scrollableElementRef={scrollableElementRef}
                    />
                ))}

                {isLoadingPermissions ? (
                    <Loader />
                ) : (
                    persistedSharedEntries.map((manifest) => (
                        <SharedEntry
                            key={`${manifest.recipient.id}-${manifest.recipient.type}`}
                            name={manifest.recipient.name}
                            parentheticalValue={getParentheticalValue(
                                manifest.recipient
                            )}
                            manifest={manifest}
                            onPermissionChange={onUpdateExistingPermission}
                            onPermissionRemove={onRemovePermission}
                            scrollableElementRef={scrollableElementRef}
                        />
                    ))
                )}
                {CollectionUtils.isEmpty(draftSharedEntries) &&
                    CollectionUtils.isEmpty(persistedSharedEntries) &&
                    !isLoadingPermissions &&
                    accessListType !== "individual" && (
                        <span className={`${CSS_CLASS_NAME}__no-list-items`}>
                            {t("noneAdded")}
                        </span>
                    )}
            </div>
        </div>
    );
};

export default NotebookShareModalAccessList;
